import { API_BASE_URL } from "../config/api";
import { getToken } from "../shared/data/storage/authStorage";
import {
  TallySyncModule,
  TallySyncReport,
} from "../features/admin/presentation/types/tallySyncReport";
import { TallySyncStatusResponse } from "../features/admin/presentation/types/tallySyncStatus";
import { ACCOUNTING_CONFIG } from "../config/accounting";

const BASE = `${API_BASE_URL}${ACCOUNTING_CONFIG.MODULE_SYNC_BASE_URL}`;

/** GL COA import can take several minutes on large books. */
const GL_SYNC_TIMEOUT_MS = 10 * 60 * 1000;
const DEFAULT_SYNC_TIMEOUT_MS = 5 * 60 * 1000;

function authHeaders(): HeadersInit {
  const token = getToken();
  return {
    Authorization: token ? `Bearer ${token}` : "",
    Accept: "application/json",
    "Content-Type": "application/json",
  };
}

async function postModuleSync(
  module: TallySyncModule,
  dryRun: boolean,
  timeoutMs: number = DEFAULT_SYNC_TIMEOUT_MS,
  fromDate?: string | null,
): Promise<TallySyncReport> {
  const params = new URLSearchParams();
  if (dryRun) {
    params.set("dry_run", "1");
  }
  if (fromDate) {
    params.set("from_date", fromDate);
  }
  const qs = params.toString() ? `?${params.toString()}` : "";
  const controller = new AbortController();
  const timer = window.setTimeout(() => controller.abort(), timeoutMs);

  try {
    const res = await fetch(`${BASE}/${module}${qs}`, {
      method: "POST",
      headers: authHeaders(),
      signal: controller.signal,
    });

    let body: TallySyncReport | null = null;
    try {
      body = (await res.json()) as TallySyncReport;
    } catch {
      body = null;
    }

    if (
      body &&
      typeof body === "object" &&
      "module" in body &&
      "summary" in body
    ) {
      return body;
    }

    throw new Error(
      body && typeof body === "object" && "message" in body
        ? String((body as { message?: string }).message)
        : `${res.status} ${res.statusText}`,
    );
  } catch (e) {
    if (e instanceof DOMException && e.name === "AbortError") {
      throw new Error(
        "Sync timed out — Tally may still be processing. Check again in a few minutes.",
      );
    }
    throw e;
  } finally {
    window.clearTimeout(timer);
  }
}

export const tallySyncApi = {
  getStatus: async (): Promise<TallySyncStatusResponse> => {
    const res = await fetch(`${BASE}/status`, {
      method: "GET",
      headers: authHeaders(),
    });

    const body = (await res.json()) as TallySyncStatusResponse;
    if (!res.ok || !body?.success) {
      throw new Error("Failed to load Tally sync status");
    }

    return body;
  },

  syncGl: (dryRun = false) => postModuleSync("gl", dryRun, GL_SYNC_TIMEOUT_MS),
  syncCustomers: (dryRun = false) => postModuleSync("customers", dryRun),
  syncVendors: (dryRun = false) => postModuleSync("vendors", dryRun),
  syncInvoices: (dryRun = false, fromDate?: string | null) =>
    postModuleSync("invoices", dryRun, DEFAULT_SYNC_TIMEOUT_MS, fromDate),
  syncBills: (dryRun = false, fromDate?: string | null) =>
    postModuleSync("bills", dryRun, DEFAULT_SYNC_TIMEOUT_MS, fromDate),
  syncJournals: (dryRun = false, fromDate?: string | null) =>
    postModuleSync("journals", dryRun, DEFAULT_SYNC_TIMEOUT_MS, fromDate),
};
